package dairy.mynotes.noteslist

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import dairy.mynotes.repository.AuthRepository
import dairy.mynotes.repository.Note
import dairy.mynotes.repository.NotesRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class NoteSection(
    val category: String,
    val notes: List<Note>
)

data class NotesListUiState(
    val sections: List<NoteSection> = emptyList(),
    val isLoading: Boolean = false,
    val error: String? = null
)

class NotesListViewModel : ViewModel() {
    private val notesRepository = NotesRepository()
    private val authRepository = AuthRepository()

    private val _uiState = MutableStateFlow(NotesListUiState())
    val uiState: StateFlow<NotesListUiState> = _uiState.asStateFlow()

    init {
        loadNotes()
    }

    fun loadNotes() {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(isLoading = true, error = null)
            val userId = authRepository.getCurrentUserId()
            if (userId == null) {
                _uiState.value = _uiState.value.copy(isLoading = false, error = "User not logged in")
                return@launch
            }
            notesRepository.getUserNotes(userId).onSuccess { notes ->
                val sections = notes
                    .groupBy { it.category.ifBlank { "Uncategorized" } }
                    .map { (category, categoryNotes) ->
                        NoteSection(
                            category = category,
                            notes = categoryNotes.sortedByDescending { it.createdAt }
                        )
                    }
                    .sortedBy { it.category }
                _uiState.value = _uiState.value.copy(isLoading = false, sections = sections)
            }.onFailure { error ->
                _uiState.value = _uiState.value.copy(isLoading = false, error = error.message)
            }
        }
    }

    fun deleteNote(noteId: String) {
        viewModelScope.launch {
            notesRepository.deleteNote(noteId).onSuccess {
                loadNotes()
            }.onFailure { e ->
                _uiState.value = _uiState.value.copy(error = e.message)
            }
        }
    }

    fun deleteCategory(category: String) {
        viewModelScope.launch {
            val userId = authRepository.getCurrentUserId() ?: return@launch
            notesRepository.deleteNotesByCategory(userId, category).onSuccess {
                loadNotes()
            }.onFailure { e ->
                _uiState.value = _uiState.value.copy(error = e.message)
            }
        }
    }
}
